// ─── Shared search box + remote-search hook ───────────────────────────────────
// SearchBox is the Congressional tab's search control (AltDataTab CongressPanel):
// full-width 44px input, "×" clear inside the right edge shown only while there is
// text. Used by Unusual Options, Ticker Confluence and Private Markets so the
// three feel the same as Congressional.
//
// useRemoteSearch runs a debounced backend search for the views whose lists are
// capped on the server (unusual options top 100, convergence tape 200), where
// filtering only the loaded rows would miss real matches.
// ⚠ STALE RESPONSES ARE DROPPED BY SEQUENCE, NOT BY ABORT. backendFetch owns its
// own AbortController (the request timeout) and overwrites any signal passed in,
// so each request takes a number and only the newest one may write state. A slow
// response for "NV" can never replace the results for "NVDA".

import { useState, useEffect, useRef, useCallback } from "react";
import { C, font } from "../core";

export function SearchBox({ value, onChange, placeholder, label }) {
  return (
    <div style={{ position: "relative", marginBottom: 8 }}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label || placeholder}
        style={{
          width: "100%", boxSizing: "border-box", minHeight: 44,
          background: C.panel, border: `1px solid ${C.border}`, color: C.text,
          fontFamily: font, fontSize: 13, padding: "8px 34px 8px 10px", borderRadius: 3,
        }}
      />
      {value && (
        <button
          onClick={() => onChange("")}
          title="Clear"
          aria-label="Clear search"
          style={{
            position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", color: C.textDim, cursor: "pointer",
            fontSize: 18, lineHeight: 1, padding: "0 6px",
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

// The backend's rule (app/services/symbol_query.py): letters, digits, dot, hyphen,
// at most 10 characters. Anything else is never sent.
export const TICKER_QUERY_RE = /^[A-Za-z0-9.\-]{1,10}$/;
export const SEARCH_DEBOUNCE_MS = 300;

// status: "idle" (box empty) | "hint" (text that will not be sent) |
//         "pending" (waiting for typing to pause) | "loading" (request in flight) |
//         "done" | "error"
// `data` belongs to `q`; a refresh of the same query keeps showing the last data
// while it runs, a new query never shows the previous query's data.
export function useRemoteSearch(query, fetcher, { delay = SEARCH_DEBOUNCE_MS, valid = (q) => TICKER_QUERY_RE.test(q) } = {}) {
  const q = String(query || "").trim();
  const [state, setState] = useState({ q: "", status: "idle", data: null });
  const seq = useRef(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const validRef = useRef(valid);
  validRef.current = valid;

  const stateRef = useRef(state);
  stateRef.current = state;

  const run = useCallback(async (term) => {
    const mine = ++seq.current;
    setState((s) => (s.q === term ? { ...s, status: s.status === "pending" ? "loading" : s.status } : s));
    try {
      const data = await fetcherRef.current(term);
      if (seq.current === mine) setState({ q: term, status: "done", data });
    } catch {
      if (seq.current === mine) setState({ q: term, status: "error", data: null });
    }
  }, []);

  useEffect(() => {
    if (!q) { seq.current++; setState({ q: "", status: "idle", data: null }); return undefined; }
    if (!validRef.current(q)) { seq.current++; setState({ q, status: "hint", data: null }); return undefined; }
    seq.current++;                       // anything in flight is now stale
    setState({ q, status: "pending", data: null });
    const t = setTimeout(() => run(q), delay);
    return () => clearTimeout(t);
  }, [q, delay, run]);

  // Re-run the current query (the views' polls call this while a search is
  // active). No-op unless the last search completed.
  const refresh = useCallback(() => {
    const s = stateRef.current;
    if (s.q && (s.status === "done" || s.status === "error")) run(s.q);
  }, [run]);

  return { ...state, active: q.length > 0, refresh };
}
